/**
 * WizardContainer — orquestrador do wizard ZTNA 4-step (Lote 7).
 *
 * - Mantem state local da WizardState completa (step1..4).
 * - Hidrata do draft existente (GET /admin/ztna/drafts/active) on-mount.
 * - Autosave debounced 1s a cada mudanca via useDraft.saveStepDebounced.
 * - Stepper Mantine top + footer back/next + Provisionar no Step 4.
 * - Submit chama POST /admin/ztna/onboard, navega de volta pra Dashboard
 *   onde o JobBanner faz polling.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Group,
  Stack,
  Stepper,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconChevronLeft, IconChevronRight, IconPlayerPlay } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ztnaApi } from '../api'
import { foiParaGmud, notifyPendingGmud } from '../gmudNotice'
import type { PreflightResponse } from '../types'
import { Step1TenantDns } from './Step1TenantDns'
import { Step2Identity } from './Step2Identity'
import { Step3Routing } from './Step3Routing'
import { Step4Review } from './Step4Review'
import { useDraft } from './useDraft'
import {
  DEFAULT_WIZARD_STATE,
  isStepComplete,
  toJobPayload,
  type WizardState,
} from './wizardState'
import { formatApiError } from '@/api/errors'

const STEP_NAMES = ['Tenant + DNS', 'Identidade', 'Routing & Identity', 'Review'] as const

interface DraftStepData {
  step1?: Partial<WizardState['step1']>
  step2?: Partial<WizardState['step2']>
  step3?: Partial<WizardState['step3']>
  step4?: Partial<WizardState['step4']>
}

function hydrateState(stepData: Record<string, unknown> | null | undefined): WizardState {
  if (!stepData) return DEFAULT_WIZARD_STATE
  const sd = stepData as DraftStepData
  return {
    step1: { ...DEFAULT_WIZARD_STATE.step1, ...(sd.step1 ?? {}) },
    step2: { ...DEFAULT_WIZARD_STATE.step2, ...(sd.step2 ?? {}) },
    step3: { ...DEFAULT_WIZARD_STATE.step3, ...(sd.step3 ?? {}) },
    step4: { ...DEFAULT_WIZARD_STATE.step4, ...(sd.step4 ?? {}) },
  }
}

export function WizardContainer() {
  const navigate = useNavigate()
  const draftHook = useDraft()
  const [activeStep, setActiveStep] = useState(0)
  const [state, setState] = useState<WizardState>(DEFAULT_WIZARD_STATE)
  const [hydrated, setHydrated] = useState(false)
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null)
  const [preflightLoading, setPreflightLoading] = useState(false)
  const [preflightError, setPreflightError] = useState<Error | null>(null)

  // Hidrata state ao receber draft do servidor.
  useEffect(() => {
    if (hydrated) return
    if (draftHook.isLoading) return
    if (draftHook.draft && draftHook.draft.step_data) {
      setState(hydrateState(draftHook.draft.step_data))
      setActiveStep(Math.max(0, (draftHook.draft.current_step || 1) - 1))
    }
    setHydrated(true)
  }, [draftHook.draft, draftHook.isLoading, hydrated])

  // Autosave debounced sempre que state muda (e ja hidratamos).
  useEffect(() => {
    if (!hydrated) return
    draftHook.saveStepDebounced(activeStep + 1, state as unknown as Record<string, unknown>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, hydrated])

  // DNS lookup — descobre se ha registro pre-existente pro public_hostname
  // e propoe upstream_url (ALB legacy). Step 3 le state.dnsLookup pra
  // auto-fill upstream_url + mostrar banner "Upstream detectado".
  //
  // A query vive aqui (e nao em Step 1) porque a request pode demorar e o
  // user pode avancar pra Step 2/3 antes dela completar — se a query
  // estivesse no Step1TenantDns, o useEffect de propagacao desmontaria
  // junto com o Step e o resultado nunca chegaria em state.dnsLookup.
  const tenantsQuery = useQuery({
    queryKey: ['ztna', 'tenants'],
    queryFn: () => ztnaApi.listTenants(),
    staleTime: 60_000,
  })
  const selectedTenant = useMemo(() => {
    if (state.step1.tenant_id == null) return null
    return (tenantsQuery.data ?? []).find((t) => t.id === state.step1.tenant_id) ?? null
  }, [tenantsQuery.data, state.step1.tenant_id])
  const needsPrepare =
    selectedTenant != null && !selectedTenant.ztna_provision_role_arn

  const dnsLookupQuery = useQuery({
    queryKey: [
      'ztna',
      'dns-lookup',
      state.step1.tenant_id,
      state.step1.zone_id,
      state.step1.public_hostname,
    ],
    queryFn: () =>
      ztnaApi.dnsLookup(
        state.step1.tenant_id!,
        state.step1.zone_id,
        state.step1.public_hostname,
      ),
    enabled:
      state.step1.tenant_id != null
      && !needsPrepare
      && Boolean(state.step1.zone_id)
      && Boolean(state.step1.public_hostname)
      && state.step1.public_hostname.includes('.'),
    staleTime: 30_000,
  })

  // Sync query data → state.dnsLookup. Usa ref pra evitar re-set quando
  // identidade do objeto muda mas conteudo nao (refetch retorna mesmo dado).
  const lastLookupKeyRef = useRef<string | null>(null)
  useEffect(() => {
    const data = dnsLookupQuery.data
    const key = data
      ? `${state.step1.tenant_id}|${state.step1.zone_id}|${state.step1.public_hostname}|${data.record_type}|${data.proposed_upstream_url ?? ''}`
      : `${state.step1.tenant_id}|${state.step1.zone_id}|${state.step1.public_hostname}|null`
    if (key === lastLookupKeyRef.current) return
    lastLookupKeyRef.current = key
    setState((s) => ({ ...s, dnsLookup: data ?? undefined }))
  }, [
    dnsLookupQuery.data,
    state.step1.tenant_id,
    state.step1.zone_id,
    state.step1.public_hostname,
  ])

  const canContinue = useMemo(() => {
    const stepN = (activeStep + 1) as 1 | 2 | 3 | 4
    return isStepComplete(stepN, state)
  }, [activeStep, state])

  const allComplete = useMemo(() => {
    return (
      isStepComplete(1, state) &&
      isStepComplete(2, state) &&
      isStepComplete(3, state) &&
      isStepComplete(4, state)
    )
  }, [state])

  function handleBack() {
    setActiveStep((s) => Math.max(0, s - 1))
  }

  function handleNext() {
    setActiveStep((s) => Math.min(3, s + 1))
  }

  async function handleRunPreflight() {
    if (!isStepComplete(1, state) || !isStepComplete(2, state) || !isStepComplete(3, state)) {
      return
    }
    setPreflightLoading(true)
    setPreflightError(null)
    try {
      const result = await draftHook.runPreflight(toJobPayload(state))
      setPreflight(result)
    } catch (err) {
      setPreflightError(err as Error)
    } finally {
      setPreflightLoading(false)
    }
  }

  async function handleSubmit() {
    if (!allComplete) return
    if (preflight && !preflight.can_submit) {
      notifications.show({
        title: 'Pre-flight com blockers',
        message: 'Corrija as falhas antes de provisionar.',
        color: 'red',
      })
      return
    }
    try {
      const result = await draftHook.submitOnboard(
        toJobPayload(state),
        state.dnsMigration,
      )
      if (foiParaGmud(result)) {
        // "Provisionamento iniciado" seria falso: o job nasceu `pending_gmud` e
        // o worker nao o reivindica. O banner do dashboard mostra o card ambar
        // de espera, entao mandar o operador para la continua correto.
        notifyPendingGmud(
          'o provisionamento',
          state.step1.public_hostname || 'a aplicação',
          result.gmud_id,
        )
      } else {
        notifications.show({
          title: 'Provisionamento iniciado',
          message: `Job #${result.job_id} disparado. Acompanhe no banner do dashboard.`,
          color: 'teal',
        })
      }
      navigate('/admin/ztna')
    } catch (err) {
      notifications.show({
        title: 'Falha ao iniciar provisionamento',
        message: formatApiError(err, 'Falha ao iniciar o provisionamento.'),
        color: 'red',
      })
    }
  }

  async function handleAbandon() {
    if (draftHook.draft) {
      try {
        await draftHook.deleteDraft()
        notifications.show({ message: 'Rascunho apagado.', color: 'gray' })
      } catch {
        // ignore
      }
    }
    navigate('/admin/ztna')
  }

  const lastSavedLabel = draftHook.lastSavedAt
    ? `Draft salvo · ${formatRelative(draftHook.lastSavedAt)}`
    : draftHook.isSaving
      ? 'Salvando...'
      : 'Sem alterações pendentes'

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2} c="var(--ai-brand-azul, #025468)">Cadastrar app ZTNA</Title>
          <Text size="sm" c="dimmed">Administração / ZTNA / Onboard</Text>
        </div>
        <Button variant="subtle" color="gray" onClick={handleAbandon}>
          Cancelar
        </Button>
      </Group>

      <Stepper active={activeStep} onStepClick={setActiveStep} allowNextStepsSelect={false}>
        {STEP_NAMES.map((name) => (
          <Stepper.Step key={name} label={name} />
        ))}
      </Stepper>

      <div>
        {activeStep === 0 ? (
          <Step1TenantDns
            data={state.step1}
            onChange={(next) => setState((s) => ({ ...s, step1: next }))}
          />
        ) : null}
        {activeStep === 1 ? (
          <Step2Identity
            data={state.step2}
            step1={state.step1}
            onChange={(next) => setState((s) => ({ ...s, step2: next }))}
          />
        ) : null}
        {activeStep === 2 ? (
          <Step3Routing
            data={state.step3}
            publicHostname={state.step1.public_hostname}
            dnsLookup={state.dnsLookup}
            onChange={(next) => setState((s) => ({ ...s, step3: next }))}
          />
        ) : null}
        {activeStep === 3 ? (
          <Step4Review
            state={state}
            preflight={preflight}
            preflightLoading={preflightLoading}
            preflightError={preflightError}
            onChangeStep4={(next) => setState((s) => ({ ...s, step4: next }))}
            onChangeDnsMigration={(next) =>
              setState((s) => ({ ...s, dnsMigration: next }))
            }
            onRunPreflight={handleRunPreflight}
          />
        ) : null}
      </div>

      <Group justify="space-between" mt="md">
        <Button
          leftSection={<IconChevronLeft size={16} />}
          variant="default"
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          Voltar
        </Button>
        <Text size="xs" c="dimmed">{lastSavedLabel}</Text>
        {activeStep < 3 ? (
          <Button
            rightSection={<IconChevronRight size={16} />}
            color="teal"
            onClick={handleNext}
            disabled={!canContinue}
          >
            Continuar
          </Button>
        ) : (
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            color="teal"
            onClick={handleSubmit}
            loading={draftHook.isSubmitting}
            disabled={
              !allComplete ||
              preflight == null ||
              preflightLoading ||
              !preflight.can_submit
            }
          >
            Provisionar agora
          </Button>
        )}
      </Group>
    </Stack>
  )
}

function formatRelative(date: Date): string {
  const elapsed = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (elapsed < 5) return 'agora ha pouco'
  if (elapsed < 60) return `ha ${elapsed}s`
  const m = Math.floor(elapsed / 60)
  return `ha ${m}min`
}

export default WizardContainer
